/**
 * El formulario de entrar, separado de la ruta para que se pueda montar sin
 * router. La ruta (`routes/login.tsx`) decide a donde ir despues; este solo
 * pide las credenciales y muestra lo que contesto el backend.
 *
 * EL MENSAJE DEL BACKEND SE MUESTRA ENTERO Y SE QUEDA EN PANTALLA
 * --------------------------------------------------------------
 * Los tres errores que contesta `/sesion/login` dicen cosas distintas y llevan a
 * acciones distintas:
 *
 *   · "Email o contraseña incorrectos."  -> probar de nuevo
 *   · "Tu usuario esta desactivado."     -> hablar con el administrador
 *   · "Demasiados intentos. Volve a probar en 47 segundos." -> esperar
 *
 * El tercero es el que mas importa: sin ese numero, alguien que se equivoco
 * cinco veces sigue intentando contra una puerta que no se abre hasta dentro de
 * un minuto, y concluye que el sistema esta roto. Por eso el error va en un
 * `Aviso` fijo debajo del formulario y no en un toast que se va solo. Los dos
 * tonos llevan `role="alert"`: el de espera tambien interrumpe, porque el
 * segundo intento es el que no hay que hacer.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";

import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { InputPassword } from "@/components/ui/input-password";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { ErrorApi, mensajeDeError } from "@/lib/api";

export interface Credenciales {
  email: string;
  password: string;
}

export interface FormularioLoginProps {
  /** Rechaza con el error del backend; el formulario lo muestra tal cual. */
  enviar: (credenciales: Credenciales) => Promise<void>;
  operando: boolean;
}

export function FormularioLogin({ enviar, operando }: FormularioLoginProps): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ErrorApi | Error | null>(null);
  const campoEmail = useRef<HTMLInputElement>(null);
  /**
   * Freno de doble envio. En el iPhone el formulario se manda dos veces en el
   * mismo segundo (el llavero completa con Face ID y envia, y el dedo toca
   * «Entrar» o «Ir»): el registro de Railway muestra los logins de a pares,
   * 10 a 250 ms entre uno y otro. `operando` no alcanza porque llega con el
   * proximo render, despues del segundo envio; una ref corta en el acto.
   */
  const enviando = useRef(false);

  useEffect(() => {
    campoEmail.current?.focus();
  }, []);

  async function alEnviar(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (enviando.current) return;
    enviando.current = true;
    setError(null);
    try {
      await enviar({ email: email.trim(), password });
    } catch (falla) {
      // El texto del backend queda tal cual. Es lo unico que le dice a la
      // persona si tiene que reintentar, esperar, o llamar a alguien.
      setError(falla instanceof Error ? falla : new Error(mensajeDeError(falla)));
      setPassword("");
    } finally {
      enviando.current = false;
    }
  }

  const detalles = error instanceof ErrorApi ? error.detalles : {};
  const bloqueado = error instanceof ErrorApi && error.estado === 429;

  return (
    <Card padding="normal">
      <TituloSeccion nivel="seccion" descripcion="Con el mail y la contraseña de tu cuenta.">
        Entrar
      </TituloSeccion>

      <form onSubmit={(e) => void alEnviar(e)} noValidate className="mt-4 space-y-4">
        <Campo label="Mail" error={detalles.email?.join(" ")}>
          <Input
            ref={campoEmail}
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="vos@manzanamordida.ar"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={operando}
          />
        </Campo>

        <Campo label="Contraseña">
          <InputPassword
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={operando}
          />
        </Campo>

        {error ? (
          <Aviso tono={bloqueado ? "warning" : "destructive"} role="alert" aria-live="assertive">
            {mensajeDeError(error)}
          </Aviso>
        ) : null}

        <Button type="submit" className="w-full" cargando={operando}>
          Entrar
        </Button>
      </form>
    </Card>
  );
}
